/**
 * Axios client for the Estate Hub platform API.
 *
 * Auth is an httpOnly `platformToken` cookie set by the API, so every request
 * needs `withCredentials` and there is no token for this app to store. A 401
 * means the console session is gone — the interceptor clears local state and
 * sends the user back to sign in.
 */
import axios from 'axios';

/** Dev proxies /api → the API (see vite.config.ts); prod uses a same-origin path. */
const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

/** Set by AuthProvider so the interceptor can drop the cached session. */
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null) => {
  onUnauthorized = fn;
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url || '';
    // A failed sign-in is a 401 too — that one belongs to the login form, not
    // the session-expired path, or the error would never render.
    if (status === 401 && !url.includes('/auth/login')) onUnauthorized?.();
    return Promise.reject(error);
  },
);

/** Pull the human-readable message the API sends, with a usable fallback. */
export const apiError = (error: unknown, fallback = 'Something went wrong'): string => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || fallback;
};

export default api;
